import base from "./dict.json";
import overrides from "./overrides.json";

const dict = { ...base, ...overrides };

let lang = localStorage.getItem("ui_lang") || "pl";
const maps = { pl: new Map(), en: new Map() };
const patterns = { pl: [], en: [] };
const cache = { pl: new Map(), en: new Map() };
const norm = (s) => s.replace(/\s+/g, " ").trim();
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

for (const [src, v] of Object.entries(dict)) {
  if (v.skip) continue;
  for (const L of ["pl", "en"]) {
    for (const k of new Set([src, v.pl, v.en])) {
      if (!k) continue;
      if (/\{\d+\}/.test(k)) {
        if (k.replace(/\{\d+\}/g, "").replace(/[^\p{L}]/gu, "").length < 3) continue;
        const order = [...k.matchAll(/\{(\d+)\}/g)].map((m) => m[1]);
        const re = new RegExp("^" + esc(k).replace(/\\\{\d+\\\}/g, "(.+?)") + "$", "s");
        patterns[L].push({ re, order, tpl: v[L] });
      } else if (!maps[L].has(k) || k === src) maps[L].set(k, v[L]);
    }
  }
}
for (const L of ["pl", "en"]) patterns[L].sort((a, b) => b.re.source.length - a.re.source.length);

export const tr = (text, L = lang) => {
  if (!text || typeof text !== "string") return text;
  const n = norm(text);
  if (!n) return text;
  const c = cache[L];
  let out = c.get(n);
  if (out === undefined) {
    out = maps[L].get(n);
    if (out === undefined) {
      for (const p of patterns[L]) {
        const m = n.match(p.re);
        if (m) { const vals = {}; p.order.forEach((k, i) => { vals[k] = m[i + 1]; }); out = p.tpl.replace(/\{(\d+)\}/g, (_, k) => vals[k] ?? ""); break; }
      }
    }
    if (out === undefined) out = null;
    c.set(n, out);
  }
  if (out === null || out === n) return text;
  const lead = text.match(/^\s*/)[0], trail = text.match(/\s*$/)[0];
  return lead + out + trail;
};

const SKIP = "[data-no-i18n],textarea,script,style,code,pre,[contenteditable=true],.ProseMirror";
const ATTRS = ["placeholder", "title", "aria-label"];

const doText = (t) => {
  const p = t.parentElement;
  if (!p || p.closest(SKIP)) return;
  const v = tr(t.nodeValue);
  if (v !== t.nodeValue) t.nodeValue = v;
};
const doAttrs = (el) => {
  for (const a of ATTRS) {
    const v = el.getAttribute?.(a);
    if (v) { const r = tr(v); if (r !== v) el.setAttribute(a, r); }
  }
};
const walk = (root) => {
  if (root.nodeType === 3) return doText(root);
  if (root.nodeType !== 1 || root.closest(SKIP)) return;
  doAttrs(root);
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n = w.nextNode();
  while (n) {
    if (n.nodeType === 3) doText(n);
    else doAttrs(n);
    n = w.nextNode();
  }
};

const obs = new MutationObserver((ms) => {
  for (const m of ms) {
    if (m.type === "characterData") doText(m.target);
    else if (m.type === "attributes") doAttrs(m.target);
    else m.addedNodes.forEach(walk);
  }
});

export const startI18n = () => {
  const swap = (proto, fn) => {
    const orig = proto[fn];
    proto[fn] = function (loc, opts) { return orig.call(this, lang === "en" && (!loc || /^pl/i.test(String(loc))) ? "en-GB" : loc, opts); };
  };
  swap(Date.prototype, "toLocaleDateString");
  swap(Date.prototype, "toLocaleString");
  swap(Date.prototype, "toLocaleTimeString");
  swap(Number.prototype, "toLocaleString");
  for (const k of ["NumberFormat", "DateTimeFormat"]) {
    const O = Intl[k];
    const W = function (loc, opts) { return new O(lang === "en" && (!loc || /^pl/i.test(String(loc))) ? "en-GB" : loc, opts); };
    W.prototype = O.prototype;
    W.supportedLocalesOf = O.supportedLocalesOf;
    Intl[k] = W;
  }
  obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  const c = window.confirm.bind(window), a = window.alert.bind(window), pr = window.prompt.bind(window);
  window.confirm = (m) => c(tr(m));
  window.alert = (m) => a(tr(m));
  window.prompt = (m, d) => pr(tr(m), d);
  document.documentElement.lang = lang;
  walk(document.body);
};

export const getLang = () => lang;
export const setLang = (L) => {
  if (!["pl", "en"].includes(L) || L === lang) return;
  lang = L;
  localStorage.setItem("ui_lang", L);
  document.documentElement.lang = L;
  walk(document.body);
};
